<template>
  <hit-modal
    :title="title || t('hit-app.common.printer-settings')"
    prefix-icon="printer"
    popup-size="small"
    :cancellable="!isPrinting"
    @cancel="emitCancel"
  >
    <div class="flex flex-col">
      <div v-if="chooseTemplate">
        <hit-app-input-template
          :value="selectedTemplate"
          :template-prefix="templateType"
          :custom-template-filter-fn="customTemplateFilterFn"
          @change="(value) => (selectedTemplate = value)"
        />
      </div>
      <div v-if="chooseLayout">
        <hit-app-input-print-layout
          :key="printLayoutSelectorKey"
          :value="selectedPrintLayout"
          :orientation-filter="printLayoutOrientationFilter"
          :layout-filter-fn="printLayoutFilterFn"
          @change="(value) => (selectedPrintLayout = value)"
        />
      </div>
      <div v-if="choosePrintLanguage">
        <hit-app-input-print-language
          :value="selectedPrintLanguage"
          @change="(value) => (selectedPrintLanguage = value)"
        />
      </div>
      <div v-if="chooseOneOrMultipleFiles">
        <hit-input-radio-button
          :options="fileOptions"
          :value="fileOptionValue"
          :hide-clear-button="true"
          @change="(v) => (fileOptionValue = v)"
        />
      </div>
      <div
        v-if="selectedTemplate === 'projectPartCostByPosition'"
        class="w-full hit-form"
        style="margin-bottom: 1rem"
      >
        <hit-group-input
          :label="t('hit-app.common.positions')"
          display-layout="column"
          class="w-full"
        >
          <hit-input-radio-button
            class="w-full"
            :options="costByPositionPositionOptions"
            :value="costByPositionPositionMode"
            :hide-clear-button="true"
            @change="onCostByPositionPositionModeChange"
          />
          <div v-if="costByPositionPositionMode === 'one'" class="w-full">
            <hit-fast-mode-browser
              class="w-full"
              :entity-class="PositionItem"
              :options="null"
              :data-service="DataService"
              :value="costByPositionPosition"
              :clearable="true"
              emit-change-to-parent
              :full-width="true"
              :adapt-to-content="false"
              :property-path-to-display="['fastModeBrowserDesignation']"
              :custom-filters="costByPositionPositionFilter"
              @item-selected="onCostByPositionPositionSelected"
            />
          </div>
        </hit-group-input>
        <hit-group-input
          :label="t('hit-app.common.cost-report-mode')"
          display-layout="column"
          class="w-full"
          style="margin-top: 0.75rem"
        >
          <hit-input-radio-button
            class="w-full"
            :options="costByPositionReportModeOptions"
            :value="configs.reportMode || 'summary'"
            :hide-clear-button="true"
            @change="(v) => changeConfigValue('reportMode', v)"
          />
        </hit-group-input>
      </div>
      <div v-if="customConfigs.length > 0">
        <div
          v-for="customConfig in customConfigs"
          :key="customConfig.id"
          class="w-full hit-form-fixed-label-width pt-0.5"
        >
          <hit-input-checkbox
            v-if="customConfig.type === 'boolean'"
            :values="t(customConfig.label)"
            :value="configs?.[customConfig.id] || customConfig.default"
            @change="(v) => changeConfigValue(customConfig.id, v)"
          />
          <hit-input-month-year
            v-else-if="customConfig.type === 'month'"
            :label="t(customConfig.label)"
            :value="getConfigValue(customConfig)"
            @change="(v) => changeConfigValue(customConfig.id, v)"
          />
          <hit-select
            v-else-if="customConfig.type === 'select'"
            :label="t(customConfig.label)"
            :options="getSelectOptions(customConfig)"
            :label-fields="['designation']"
            :value="getConfigValue(customConfig)"
            :adapt-to-content="false"
            :full-width="true"
            :clearable="!customConfig.required"
            @change="(v) => changeConfigValue(customConfig.id, v)"
          />
          <div
            v-else-if="
              customConfig.type === 'entity' && getEntityClass(customConfig)
            "
            class="w-full custom-report-entity-field col-span-2"
          >
            <hit-fast-mode-browser
              class="w-full"
              :adapt-to-content="false"
              :full-width="true"
              :label="t(customConfig.label)"
              :entity-class="getEntityClass(customConfig)"
              :options="null"
              :data-service="DataService"
              :value="entitySelections[customConfig.id] || null"
              :clearable="!customConfig.required"
              emit-change-to-parent
              :property-path-to-display="customConfig.propertyPathToDisplay"
              :custom-filters="getFilter(customConfig)"
              @item-selected="
                (item) => onEntityConfigSelected(customConfig, item)
              "
            />
          </div>
          <hit-input
            v-else
            :label="t(customConfig.label)"
            :type="customConfig.type"
            :value="getConfigValue(customConfig)"
            @change="(v) => changeConfigValue(customConfig.id, v)"
            @input="(v) => changeConfigValue(customConfig.id, v)"
          />
        </div>
      </div>
      <div
        v-if="selectedTemplate === 'planningCalendarMonthly'"
        class="w-full hit-form hit-form-fixed-label-width planning-calendar-monthly-config"
        style="margin-bottom: 2.5rem"
      >
        <hit-group-input
          :label="t('hit-app.timeslot.calendar-staff-label')"
          display-layout="column"
        >
          <hit-input-radio-button
            :options="calendarMonthlyStaffOptions"
            :value="calendarMonthlyStaffMode"
            :hide-clear-button="true"
            @change="onCalendarMonthlyStaffModeChange"
          />
          <div
            v-if="calendarMonthlyStaffMode === 'one'"
            class="w-full flex justify-end"
          >
            <hit-fast-mode-browser
              :entity-class="Staff"
              :options="null"
              :data-service="DataService"
              :value="calendarMonthlyStaff"
              :clearable="true"
              emit-change-to-parent
              :property-path-to-display="['designation']"
              @item-selected="onCalendarMonthlyStaffSelected"
            />
          </div>
        </hit-group-input>
      </div>
      <div v-if="showStockSelection" class="w-full hit-form">
        <hit-group-input
          :label="t('hit-base.common.stock')"
          display-layout="column"
        >
          <hit-input-checkbox
            :values="stockCheckboxOptions"
            :value="selectedStockIds"
            inline-input
            @change="onStockSelectionChange"
          />
        </hit-group-input>
      </div>
      <div
        v-if="showStaffHoursSelection"
        class="w-full hit-form hit-form-fixed-label-width"
      >
        <hit-browser-selection-display
          :label="t('hit-base.common.staff')"
          :designation="staffHoursSelectionDesignation"
          :clearable="true"
          @search="staffHoursBrowserVisible = true"
          @clear="clearStaffHoursSelection"
        />
      </div>
      <div
        v-if="selectedTemplate === 'listOfResources'"
        class="w-full hit-form hit-form-fixed-label-width"
      >
        <div class="mb-2 font-medium">
          {{ t('hit-app.resource.resources') }}
        </div>
        <hit-group-input>
          <hit-input-radio-button
            :options="resourceOptions"
            :value="resourceMode"
            :hide-clear-button="true"
            @change="onResourceModeChange"
          />
          <div v-if="resourceMode === 'range'" class="w-full">
            <hit-fast-mode-browser
              :label="t('hit-app.resource.from-res')"
              :entity-class="Resource"
              :options="null"
              :data-service="DataService"
              :value="resourceFrom"
              :full-width="true"
              :adapt-to-content="false"
              :property-path-to-display="['fastModeBrowserDesignation']"
              emit-change-to-parent
              :only-load-active-records="false"
              @item-selected="onResourceFromSelected"
            />
            <hit-fast-mode-browser
              :label="t('hit-app.resource.to-res')"
              :entity-class="Resource"
              :options="null"
              :data-service="DataService"
              :value="resourceTo"
              :full-width="true"
              :adapt-to-content="false"
              :property-path-to-display="['fastModeBrowserDesignation']"
              emit-change-to-parent
              :only-load-active-records="false"
              @item-selected="onResourceToSelected"
            />
          </div>
          <div class="w-full">
            <hit-input-checkbox
              :values="t('hit-app.resource.supplier-res')"
              :value="showSupplierFilter"
              inline-input
              @change="onSupplierFilterChange"
            />
            <div v-if="showSupplierFilter">
              <hit-fast-mode-browser
                :label="t('hit-app.resource.supplier')"
                :entity-class="Address"
                :options="null"
                :data-service="DataService"
                :value="selectedSupplier"
                :full-width="true"
                :adapt-to-content="false"
                :property-path-to-display="['designation']"
                :custom-filters="{
                  or: '(supplier.is.true,potential_supplier.is.true)',
                }"
                emit-change-to-parent
                @item-selected="onSupplierSelected"
              />
            </div>
          </div>
          <div class="w-full">
            <hit-input-checkbox
              :values="t('hit-app.resource.active-res')"
              :value="showActiveResources"
              inline-input
              @change="onActiveResources"
            />
          </div>
        </hit-group-input>
      </div>
      <div class="w-full">
        <div class="flex flex-col sm:flex-row sm:justify-end items-end">
          <div class="mb-2 sm:mb-0 sm:mr-4">
            <hit-button
              :label="t('hit-app.printer-configuration.set-as-default')"
              color="accent"
              prefix-icon="save"
              @click="setDefaultLayout"
            />
          </div>
          <div>
            <hit-button
              :disabled="!readyToPrint"
              :label="confirmLabel || t('hit-base.common.print')"
              color="accent"
              :prefix-icon="confirmIcon || 'printer'"
              :show-spinner="isPrinting"
              @click="printFiles"
            />
          </div>
        </div>
      </div>
    </div>
  </hit-modal>
  <hit-dynamic-browser
    :visible="staffHoursBrowserVisible"
    browser-type="staff"
    multiple
    selected-all-by-default
    :only-load-active-records="true"
    :validate-function="selectStaffHoursStaff"
    @cancel="staffHoursBrowserVisible = false"
  />
</template>

<script>
import {
  HitButton,
  HitIcon,
  HitInput,
  HitInputCheckbox,
  HitInputMonthYear,
  HitInputRadioButton,
  HitSelect,
  HitModal,
} from '@hit/components';
import HitAppInputPrintLayout from '@hit/base/src/components/hit/form/input/HitAppInputPrintLayout.vue';
import HitAppInputPrintLanguage from '@hit/base/src/components/hit/form/input/HitAppInputPrintLanguage.vue';
import {useI18n} from 'vue-i18n';
import HitAppInputTemplate from '@hit/base/src/components/hit/form/input/HitAppInputTemplate.vue';
import {
  ActionService,
  DataService,
  Staff,
  PositionItem,
  Resource,
  Address,
  HitBrowserSelectionDisplay,
  HitDynamicBrowser,
  useConfigurationStore,
  useUserProfileStore,
  ReportsLayouts,
  useNotificationsStore,
} from '@hit/base';
import HitSpinner from '@hit/components/src/components/icon/HitSpinner.vue';
import HitFastModeBrowser from '@hit/components/src/components/form/input/HitFastModeBrowser.vue';
import HitGroupInput from '@hit/components/src/components/form/input/HitGroupInput.vue';
import {HitUtils} from '@hit/components/src/utils/hit/HitUtils';
import {PrintConfigService} from './PrintConfigService';
import {CustomReportRunner} from './CustomReportRunner';

export default {
  name: 'HitAppPrintConfiguration',
  components: {
    HitIcon,
    HitModal,
    HitAppInputPrintLanguage,
    HitAppInputPrintLayout,
    HitButton,
    HitAppInputTemplate,
    HitInputRadioButton,
    HitSpinner,
    HitInput,
    HitInputCheckbox,
    HitInputMonthYear,
    HitSelect,
    HitFastModeBrowser,
    HitGroupInput,
    HitBrowserSelectionDisplay,
    HitDynamicBrowser,
  },
  setup() {
    const {t} = useI18n();
    const config = useConfigurationStore();
    const notifications = useNotificationsStore();
    const userProfile = useUserProfileStore();
    return {t, config, notifications, userProfile};
  },
  props: {
    chooseTemplate: {
      type: Boolean,
      default: true,
    },
    chooseLayout: {
      type: Boolean,
      default: true,
    },

    /**
     * Defines the templates the user can select from(form, transaction, etc.)
     */
    templateType: {
      type: String,
      required: false,
      default: 'form',
    },

    /**
     * Array of the UUIDs we want to print
     */
    idsToPrint: {
      type: [String, Array],
      required: false,
      default: () => {},
    },

    /**
     * Function to filter the templates depending on other states
     */
    customTemplateFilterFn: {
      type: Function,
      required: false,
      default: null,
    },
    /**
     * Optional custom title for the modal (defaults to printer settings)
     */
    title: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Optional custom label for the main action button (defaults to print)
     */
    confirmLabel: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Optional custom icon for the main action button (defaults to printer)
     */
    confirmIcon: {
      type: String,
      required: false,
      default: null,
    },
    customConfigsInitialValues: {
      type: Object,
      required: false,
      default: null,
    },
    /**
     * When true, confirms the modal by only emitting print config without
     * generating any report task.
     */
    emitConfigOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Options provided by the parent view, keyed by config id.
     * e.g. { stockIDs: [{ id, no, designation, title }] }
     */
    printConfigOptions: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },

  data() {
    return {
      isPrinting: false,
      configs: {},
      selectedTemplate: null,
      selectedPrintLayout: null,
      selectedPrintLanguage: null,
      selectedPlanning: null,
      calendarMonthlyStaffMode: 'all',
      calendarMonthlyStaff: null,
      entitySelections: {},
      Staff,
      PositionItem,
      Resource,
      Address,
      DataService,
      fileOptions: [
        {
          label: this.t('hit-app.printer-configuration.single-file'),
          id: 'single-file',
        },
        {
          label: this.t('hit-app.printer-configuration.multiple-files'),
          id: 'multiple-files',
        },
      ],
      fileOptionValue: 'single-file',

      resourceMode: 'all',
      resourceFrom: null,
      resourceTo: null,
      costByPositionPositionMode: 'all',
      costByPositionPosition: null,
      showSupplierFilter: false,
      selectedSupplier: null,
      selectedStockIds: [],
      loadedStocks: [],
      selectedStaffIds: [],
      staffHoursSelectionDesignation: '',
      staffHoursBrowserVisible: false,
      showActiveResources: true,
    };
  },
  mounted() {
    this.setDefaultTemplateFromStore();
  },
  computed: {
    /**
     * The choice between one single file or multiple files can only be done when
     * the user selected multiple files to print.
     */
    chooseOneOrMultipleFiles() {
      if (this.selectedTemplate === 'staffHours') {
        return false;
      }
      return Array.isArray(this.idsToPrint) && this.idsToPrint.length > 1;
    },

    /**
     * In soe cases, we can choose the language the pdf should be printed in
     */
    choosePrintLanguage() {
      return ['form'].includes(this.templateType);
    },

    /**
     * Following the type of report, we use different routes to generate the data
     */
    reportActionRoute() {
      if (this.customReportDefinition) {
        return 'report/custom';
      }

      switch (this.templateType) {
        case 'form':
          return 'report/form';
        case 'transaction':
          return 'report/transaction';
        case 'transactionValidation':
          return 'report/transaction';
        case 'transactionDashboard':
          if (this.selectedTemplate === 'SalesSummary') {
            return 'report/transaction-dashboard/sales-summary';
          }
          if (this.selectedTemplate === 'PurchaseSummary') {
            return 'report/transaction-dashboard/purchase-summary';
          }
          return 'report/transaction-dashboard';
        case 'periodicBill':
          if (this.selectedTemplate === 'periodicBillDefault') {
            return 'report/periodic-bill';
          } else if (
            this.selectedTemplate === 'periodicBillExcessSavingStatement'
          ) {
            return 'report/periodic-bill-excess-saving-statement';
          } else {
            return 'report/periodic-bill-measurement-sheet';
          }
        case 'project':
          return 'report/check-in-at-work';
        case 'projectPart':
          if (this.selectedTemplate === 'projectPartCostSummary') {
            return 'report/project-part-cost-summary';
          } else if (this.selectedTemplate === 'projectPartCostDetails') {
            return 'report/project-part-cost-details';
          } else if (this.selectedTemplate === 'projectPartCostByPosition') {
            return 'report/project-part-cost-by-position';
          } else if (this.selectedTemplate === 'projectPartResources') {
            return 'report/project-part-resources';
          } else if (
            this.selectedTemplate === 'projectPartResourceAllocationToItems'
          ) {
            return 'report/project-part-resource-allocation-to-items';
          } else if (
            this.selectedTemplate === 'requiredResourcesForProjectPart'
          ) {
            return 'report/required-resources-for-project-part';
          } else if (
            this.selectedTemplate === 'projectPartCo2EmissionOfPlannedResources'
          ) {
            return 'report/project-part-co2-emission-of-planned-resources';
          } else if (
            this.selectedTemplate === 'projectPartOfferConsumptionProgress'
          ) {
            return 'report/project-part-offer-consumption-progress';
          } else {
            return 'report/project-part';
          }
        case 'priceRequest':
          return 'report/price-request';
        case 'actionSummary':
          return 'report/action-summary';
        case 'purchase':
          if (this.selectedTemplate === 'purchaseOrder') {
            return 'report/purchase-order';
          } else {
            return 'report/purchase-supplier-price-comparison';
          }
        case 'equipment':
          if (this.selectedTemplate === 'equipmentCostSummary') {
            return 'report/equipment-cost-summary';
          } else {
            return 'report/equipment-cost-details';
          }
        case 'staff':
          if (this.selectedTemplate === 'staffIdentitySheet') {
            return 'report/staff-identity-sheet';
          } else if (this.selectedTemplate === 'staffCostSummary') {
            return 'report/staff-cost-summary';
          } else if (this.selectedTemplate === 'staffHoursDetails') {
            return 'report/staff-hours-details';
          } else if (this.selectedTemplate === 'staffHoursPerDayType') {
            return 'report/staff-hours-per-day-type';
          } else if (this.selectedTemplate === 'staffHours') {
            return 'report/staff-hours';
          } else {
            return 'report/staff-cost-details';
          }
        case 'address':
          return 'report/address-identity-sheet';
        case 'timeslot':
          return 'report/timeslot';
        case 'resourceList':
          if (this.selectedTemplate === 'stockResourceLabels') {
            return 'report/stock-resource-labels';
          }
          if (this.selectedTemplate === 'listOfResources') {
            return 'report/list-of-resources';
          }
          return 'report/list-of-composite-resources';
        case 'stock':
          if (this.selectedTemplate === 'stockResourceLabels') {
            return 'report/stock-resource-labels';
          }
          return 'report/stock-resources-list';
        case 'deliveryNoteList':
          return 'report/delivery-note';
        default:
          throw Error('Unsupported template type for the print config');
      }
    },

    /**
     * Flag that checks if all the needed values have been selected -> when true, print button is disabled
     */
    readyToPrint() {
      const baseReady =
        (!this.chooseTemplate || this.selectedTemplate) &&
        (!this.chooseLayout || this.selectedPrintLayout) &&
        (!this.choosePrintLanguage || this.selectedPrintLanguage);
      if (!baseReady) return false;
      if (
        this.selectedTemplate === 'planningCalendarMonthly' &&
        this.calendarMonthlyStaffMode === 'one'
      ) {
        if (!this.configs.staffId || this.configs.staffId === '*') return false;
      }
      if (
        this.selectedTemplate === 'projectPartCostByPosition' &&
        this.costByPositionPositionMode === 'one'
      ) {
        if (!this.configs.positionId || this.configs.positionId === '*') {
          return false;
        }
      }
      if (this.showStaffHoursSelection && this.selectedStaffIds.length === 0) {
        return false;
      }
      for (const config of this.customConfigs) {
        if (!config.required) continue;
        const value = this.configs?.[config.id];
        if (value === null || value === undefined || value === '') {
          return false;
        }
      }
      if (
        PrintConfigService.hasInvalidDateRange(this.customConfigs, this.configs)
      ) {
        return false;
      }
      return true;
    },

    /**
     * Returns a list of custom configs that need to be displayed in the print
     * configuration modal
     */
    customConfigs() {
      const configs = PrintConfigService.getTemplateConfigs(
        this.selectedTemplate,
        this.customReportDefinition,
      );
      return configs.filter((config) => {
        if (!config.moduleKeys || config.moduleKeys.length === 0) {
          return true;
        }
        return config.moduleKeys.every((moduleKey) =>
          this.userProfile.hasAccessToModule(moduleKey),
        );
      });
    },

    customReportDefinition() {
      return this.config.getCustomReportDefinition(
        this.templateType,
        this.selectedTemplate,
      );
    },

    calendarMonthlyStaffOptions() {
      return [
        {label: this.t('hit-app.timeslot.calendar-staff-all'), id: 'all'},
        {label: this.t('hit-app.timeslot.calendar-staff-one'), id: 'one'},
      ];
    },

    costByPositionPositionOptions() {
      return [
        {label: this.t('hit-app.project-part.all-positions'), id: 'all'},
        {label: this.t('hit-app.project-part.one-position'), id: 'one'},
      ];
    },

    costByPositionReportModeOptions() {
      return [
        {
          label: this.t('hit-app.common.cost-report-mode-summary'),
          id: 'summary',
        },
        {
          label: this.t('hit-app.common.cost-report-mode-detail'),
          id: 'detail',
        },
      ];
    },

    costByPositionPositionFilter() {
      const id = this.idsToPrint?.[0];
      if (id == null) {
        return null;
      }
      return {
        project_part_id: `eq.${id}`,
        and: '(item_type.neq.COMMENT,item_type.neq.TITLE)',
      };
    },

    /**
     * Narrow the print layout selector to landscape for reports whose Jinja template
     * forces landscape (@page … landscape). Otherwise undefined = all layouts.
     */
    printLayoutOrientationFilter() {
      const landscapeTemplates = [
        'planningCalendarMonthly',
        'staffHoursPerDayType',
        'transactionSubmission',
        'projectPartSubmission',
        'projectPartOfferConsumptionProgress',
      ];
      return landscapeTemplates.includes(this.selectedTemplate)
        ? 'landscape'
        : undefined;
    },

    printLayoutFilterFn() {
      if (this.selectedTemplate !== 'stockResourceLabels') {
        return null;
      }
      return (layout) =>
        PrintConfigService.isStockResourceLabelsLayoutAllowed(layout);
    },

    printLayoutSelectorKey() {
      return `${this.templateType}-${this.selectedTemplate || 'none'}`;
    },

    resourceOptions() {
      return [
        {
          label: this.t('hit-app.resource.all-resource'),
          id: 'all',
        },
        {
          label: this.t('hit-app.resource.choose-resource'),
          id: 'range',
        },
      ];
    },

    stockCheckboxOptions() {
      const stocks = this.printConfigOptions?.stockIDs?.length
        ? this.printConfigOptions.stockIDs
        : this.loadedStocks;
      return stocks.map((stock) => ({
        id: stock.id,
        label:
          stock.title ||
          `${stock.no ?? stock.number ?? ''} - ${stock.designation ?? ''}`,
      }));
    },

    showStockSelection() {
      return (
        this.selectedTemplate === 'stockResourcesList' ||
        this.selectedTemplate === 'stockResourceLabels' ||
        this.selectedTemplate === 'listOfResources'
      );
    },

    showStaffHoursSelection() {
      return this.selectedTemplate === 'staffHours';
    },
  },

  methods: {
    setDefaultTemplateFromStore() {
      if (this.templateType === 'timeslot') {
        const c = useConfigurationStore().getConfiguration;
        this.selectedPlanning = c('planning.selected-tab', 'staff');
        if (this.selectedPlanning === 'staff') {
          this.selectedTemplate = 'planningStaff';
        } else if (this.selectedPlanning === 'equipment') {
          this.selectedTemplate = 'planningEquipment';
        } else if (this.selectedPlanning === 'project_part') {
          this.selectedTemplate = 'planningProjectPart';
        }
      }
    },
    /**
     * Initialises the config variable with the default values
     */
    initConfigs() {
      this.entitySelections = {};
      this.customConfigs.forEach((config) => {
        this.configs[config.id] = this.resolveConfigInitialValue(config);
      });
      if (this.selectedTemplate === 'planningCalendarMonthly') {
        this.configs.staffId = this.configs.staffId || '*';
        this.calendarMonthlyStaffMode = 'all';
        this.calendarMonthlyStaff = null;
      }
      if (this.selectedTemplate === 'projectPartCostByPosition') {
        this.configs.positionId = this.configs.positionId || '*';
        this.configs.reportMode = this.configs.reportMode || 'summary';
        this.costByPositionPositionMode = 'all';
        this.costByPositionPosition = null;
      }
      if (this.selectedTemplate === 'listOfResources') {
        this.configs.resourceFromId = '*';
        this.configs.resourceToId = '*';
        this.resourceMode = 'all';
        this.resourceFrom = null;
        this.resourceTo = null;
      }
      if (this.showStockSelection) {
        this.ensureStocksLoaded();
        const initialStockIds = this.customConfigsInitialValues?.stockIDs;
        this.selectedStockIds = Array.isArray(initialStockIds)
          ? [...initialStockIds]
          : [];
        this.updateStockIDsConfig();
      }
      if (this.showStaffHoursSelection) {
        this.initStaffHoursSelection();
      } else {
        this.clearStaffHoursSelection();
      }
    },

    onCalendarMonthlyStaffModeChange(mode) {
      this.calendarMonthlyStaffMode = mode;
      if (mode === 'all') {
        this.changeConfigValue('staffId', '*');
        this.calendarMonthlyStaff = null;
      } else {
        this.changeConfigValue('staffId', this.calendarMonthlyStaff?.id || '');
      }
    },

    onCalendarMonthlyStaffSelected(staff) {
      this.calendarMonthlyStaff = staff;
      this.changeConfigValue('staffId', staff?.id ?? '');
    },

    onCostByPositionPositionModeChange(mode) {
      this.costByPositionPositionMode = mode;
      if (mode === 'all') {
        this.changeConfigValue('positionId', '*');
        this.costByPositionPosition = null;
      } else {
        this.changeConfigValue(
          'positionId',
          this.costByPositionPosition?.id || '',
        );
      }
    },

    onCostByPositionPositionSelected(position) {
      this.costByPositionPosition = position;
      this.changeConfigValue('positionId', position?.id ?? '');
    },

    onResourceModeChange(mode) {
      this.resourceMode = mode;

      if (mode === 'all') {
        this.resourceFrom = null;
        this.resourceTo = null;

        this.changeConfigValue('resourceFromId', '*');
        this.changeConfigValue('resourceToId', '*');
      } else {
        this.changeConfigValue('resourceFromId', '');
        this.changeConfigValue('resourceToId', '');
      }
    },

    onResourceFromSelected(res) {
      this.resourceFrom = res;
      this.changeConfigValue('resourceFromId', res?.id ?? '');
    },

    onResourceToSelected(res) {
      this.resourceTo = res;
      this.changeConfigValue('resourceToId', res?.id ?? '');
    },

    onActiveResources(value) {
      if (!value) {
        this.changeConfigValue('showActiveRes', 'false');
      } else {
        this.changeConfigValue('showActiveRes', 'true');
      }
    },

    onSupplierFilterChange(value) {
      this.showSupplierFilter = value;
      if (!value) {
        this.selectedSupplier = null;
        this.changeConfigValue('supplierId', '*');
      } else {
        this.changeConfigValue('supplierId', '');
      }
    },

    onSupplierSelected(supplier) {
      this.selectedSupplier = supplier;
      this.changeConfigValue('supplierId', supplier?.id ?? '');
    },

    async ensureStocksLoaded() {
      if (
        this.printConfigOptions?.stockIDs?.length ||
        this.loadedStocks.length
      ) {
        return;
      }
      try {
        const response = await DataService.read('stock', {
          attributes: 'id,no,designation',
          order: 'no.asc',
        });
        this.loadedStocks = (response.data || []).map((stock) => ({
          ...stock,
          title: `${stock.no} - ${stock.designation}`,
        }));
      } catch (error) {
        console.error('Error loading stocks:', error);
        this.loadedStocks = [];
      }
    },

    onStockSelectionChange(stockIds) {
      this.selectedStockIds = Array.isArray(stockIds) ? stockIds : [];
      this.updateStockIDsConfig();
    },

    updateStockIDsConfig() {
      if (!this.selectedStockIds.length) {
        if (this.templateType === 'resourceList') {
          if (this.configs && 'stockIDs' in this.configs) {
            const nextConfigs = {...this.configs};
            delete nextConfigs.stockIDs;
            this.configs = nextConfigs;
          }
          return;
        }
        this.changeConfigValue('stockIDs', '*');
        return;
      }
      this.changeConfigValue('stockIDs', [...this.selectedStockIds]);
    },

    validIdsToPrint() {
      const ids = Array.isArray(this.idsToPrint)
        ? this.idsToPrint
        : this.idsToPrint
          ? [this.idsToPrint]
          : [];
      return ids.filter((id) => id && id !== '*');
    },

    async initStaffHoursSelection() {
      const filters = {active: 'eq.true'};
      const preselected = this.validIdsToPrint();
      if (preselected.length > 0) {
        filters.id = `in.(${preselected.join(',')})`;
      }
      const res = await DataService.read('staff', {
        attributes: 'id,no,first_name,last_name',
        filters,
        order: 'no.asc',
        limit: 10000,
      });
      this.applyStaffHoursSelection(res?.data || []);
    },

    selectStaffHoursStaff(selectedStaff) {
      this.applyStaffHoursSelection(selectedStaff || []);
      this.staffHoursBrowserVisible = false;
      return true;
    },

    clearStaffHoursSelection() {
      this.selectedStaffIds = [];
      this.staffHoursSelectionDesignation = '';
      this.staffHoursBrowserVisible = false;
    },

    applyStaffHoursSelection(selectedStaff) {
      const items = Array.isArray(selectedStaff) ? selectedStaff : [];
      this.selectedStaffIds = items.map((item) => item.id).filter(Boolean);
      if (items.length === 0) {
        this.staffHoursSelectionDesignation = '';
        return;
      }
      const firstDesignation = this.createStaffHoursDesignation(items[0]);
      this.staffHoursSelectionDesignation =
        items.length > 1
          ? `${firstDesignation} ( + ${items.length - 1})`
          : firstDesignation;
    },

    createStaffHoursDesignation(staff) {
      const name =
        staff?.name ||
        `${staff?.firstName || staff?.first_name || ''} ${
          staff?.lastName || staff?.last_name || ''
        }`.trim();
      return HitUtils.createStandardDesignation(
        {
          number: staff?.number ?? staff?.no,
          name,
        },
        'number',
        'name',
      );
    },

    /**
     * Updates the value of changed configuration in the configs dictionary
     */
    changeConfigValue(configId, value) {
      if (
        configId === 'month' &&
        (typeof value !== 'string' || value.length < 7)
      ) {
        return;
      }
      this.configs[configId] = value;
    },

    /**
     * Returns the display value for a config (e.g. YYYY-MM for month type)
     */
    getConfigValue(config) {
      const raw = this.configs?.[config.id] ?? config.default;
      if (config.id === 'month' && config.type === 'month') {
        if (raw instanceof Date) {
          const y = raw.getFullYear();
          const m = String(raw.getMonth() + 1).padStart(2, '0');
          return `${y}-${m}`;
        }
        if (typeof raw === 'string' && raw.length >= 7) {
          return raw.slice(0, 7);
        }
      }
      return raw;
    },

    getSelectOptions(config) {
      return (config.options || []).map((option) => ({
        id: option.id,
        designation: option.label ? this.t(option.label) : option.id,
      }));
    },

    getEntityClass(config) {
      const entity = String(config.entity || '').toLowerCase();
      if (entity === 'staff') {
        return Staff;
      }

      if (entity === 'position-item') {
        return PositionItem;
      }

      if (entity === 'resource') {
        return Resource;
      }
      return null;
    },

    getFilter(config) {
      const id = this.idsToPrint?.[0];
      let filter = null;
      if (
        this.selectedTemplate === 'projectPartResourceAllocationToItems' &&
        id != null
      ) {
        if (config.id === 'fromItemID' || config.id === 'toItemID') {
          filter = {
            project_part_id: `eq.${id}`,
          };
        }
      }

      if (this.selectedTemplate === 'listOfCompositeResources') {
        if (config.id === 'fromResID' || config.id === 'toResID') {
          filter = {
            composite: `eq.${true}`,
          };
        }
      }
      if (
        this.selectedTemplate === 'requiredResourcesForProjectPart' &&
        id != null
      ) {
        if (config.id === 'fromItemID' || config.id === 'toItemID') {
          const conditions = ['item_type.neq.COMMENT', 'option.neq.true'];
          filter = {
            project_part_id: `eq.${id}`,
            and: `(${conditions.join(',')})`,
          };
        }
      }

      return filter;
    },

    onEntityConfigSelected(config, selectedItem) {
      this.entitySelections[config.id] = selectedItem || null;
      this.changeConfigValue(config.id, selectedItem?.id ?? '');
    },

    getRouteContext() {
      return {
        name: this.$route?.name || null,
        path: this.$route?.path || null,
        params: this.$route?.params || {},
        query: this.$route?.query || {},
        planningSelectedDay:
          sessionStorage.getItem('timeslot-planning-selected-day') || null,
      };
    },

    resolveConfigInitialValue(config) {
      const source = String(config?.source || '').toLowerCase();
      const sourceKey = config?.sourceKey;
      const routeContext = this.getRouteContext();

      let value = config.default;
      if (
        this.customConfigsInitialValues &&
        config.id in this.customConfigsInitialValues
      ) {
        value = this.customConfigsInitialValues[config.id];
      }
      if (source === 'route.param' && sourceKey) {
        value = routeContext.params?.[sourceKey];
      } else if (source === 'route.query' && sourceKey) {
        value = routeContext.query?.[sourceKey];
      } else if (source === 'sessionstorage' && sourceKey) {
        value = sessionStorage.getItem(sourceKey);
      } else if (source === 'selecteditems.first') {
        value = Array.isArray(this.idsToPrint)
          ? this.idsToPrint[0]
          : this.idsToPrint;
      } else if (source === 'now.date') {
        value = new Date().toISOString().slice(0, 10);
      }
      if (value === null || value === undefined || value === '') {
        return config.sourceDefault ?? config.default;
      }
      return value;
    },

    /**
     * Method executed when the user clicks on the print button in the modal.
     * During the print process, we disable buttons and cancel and display spinner
     */
    async printFiles(downloadPdf = true) {
      if (!this.readyToPrint) {
        return;
      }
      if (this.showStockSelection) {
        this.updateStockIDsConfig();
      }
      this.isPrinting = true;
      try {
        if (this.selectedTemplate) {
          void this.config
            .setLatestPrintTemplate(this.templateType, this.selectedTemplate)
            .catch(() => undefined);
        }
        const printConfigPayload = {
          templateId: this.selectedTemplate,
          pageLayoutId: this.selectedPrintLayout,
          preferredLanguage: this.selectedPrintLanguage,
          templateConfigs: this.configs,
          idsToPrint: this.idsToPrint,
          fileOptionValue: this.fileOptionValue,
        };

        if (this.emitConfigOnly) {
          this.$emit('printed', printConfigPayload);
          return;
        }

        let file = undefined;
        switch (this.fileOptionValue) {
          case 'single-file':
            file = await this.printInSingleFile(downloadPdf);
            break;
          case 'multiple-files':
            await this.printInMultipleFiles(downloadPdf);
            break;
        }
        if (downloadPdf) {
          this.$emit('printed', printConfigPayload);
        } else {
          return file;
        }
      } catch (error) {
        this.notifications.insertBannerNotification(
          this.t('hit-base.banners.unknown-error-title'),
        );
        throw error;
      } finally {
        this.isPrinting = false;
      }
    },

    /**
     * Prints all the selected files in one single PDF
     */
    async printInSingleFile(downloadPdf = true) {
      return await this.printFile(this.idsToPrint, downloadPdf);
    },

    /**
     * Uses one PDF per item to print. For each does not work for async operations
     */
    async printInMultipleFiles(downloadPdf = true) {
      for (const item of this.idsToPrint) {
        await this.printFile([item], downloadPdf);
      }
    },

    /**
     * Deposits the task in the slow tasks queue so that the consumer can generate the report
     * Report will be available via two buttons in the notification
     */
    async printFile(items) {
      let itemsToPrint = items;
      if (
        this.templateType === 'resourceList' &&
        this.selectedTemplate === 'stockResourceLabels' &&
        (!Array.isArray(items) ||
          items.length === 0 ||
          items.every((item) => !item || item === '*'))
      ) {
        itemsToPrint = ['*'];
      }
      if (this.selectedTemplate === 'staffHours') {
        itemsToPrint = this.selectedStaffIds;
      }

      const payload = {
        route: this.reportActionRoute,
        itemsToPrint,
        templateId: this.selectedTemplate,
        pageLayout: this.selectedPrintLayout,
        preferredLanguage: this.selectedPrintLanguage,
        templateConfigs:
          this.selectedTemplate === 'staffHours' ||
          this.templateType === 'timeslot'
            ? {
                ...this.configs,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              }
            : this.configs,
      };

      if (this.customReportDefinition) {
        let customResult = null;
        if (this.customReportDefinition.dataScriptFileId) {
          customResult = await CustomReportRunner.run({
            scriptId: this.customReportDefinition.dataScriptFileId,
            params: {
              ...this.configs,
              _itemsToPrint: itemsToPrint,
              _routeContext: this.getRouteContext(),
            },
          });
        }
        payload.templateId =
          this.customReportDefinition.templateFileId || this.selectedTemplate;
        payload.templateType =
          this.templateType === 'transactionValidation'
            ? 'transaction'
            : this.templateType;
        payload.customReportId =
          this.customReportDefinition.id || this.selectedTemplate;
        payload.customScriptFileId =
          this.customReportDefinition.dataScriptFileId || null;
        payload.customData = customResult?.data ?? {};
        payload.customName = customResult?.name ?? null;
        payload.templateConfigs = {
          ...(this.configs || {}),
          ...(customResult?.config || {}),
        };
      }
      return ActionService.depositTask('generate-report', payload);
    },

    setDefaultLayout() {
      ReportsLayouts.setAsDefault(
        this.selectedPrintLayout,
        this.selectedTemplate,
        true,
      );
    },

    /**
     * We handle the cancel event differently when the report is being printed or not
     */
    emitCancel() {
      if (!this.isPrinting) {
        this.$emit('cancel');
      }
    },
  },
  watch: {
    selectedTemplate(newValue) {
      this.initConfigs();
      if (this.customReportDefinition?.defaultLayoutId) {
        this.selectedPrintLayout = this.customReportDefinition.defaultLayoutId;
        return;
      }
      DataService.read('config', {
        attributes: 'value',
        filters: {
          label: 'eq.report.config',
          name: `eq.${newValue}`,
        },
      }).then((res) => {
        if (res && res.data && res.data.length > 0) {
          this.selectedPrintLayout = res.data[0].value.layout_id;
        }
      });
    },
  },
};
</script>

<style scoped>
.hit-form-fixed-label-width {
  grid-template-columns: 1fr 10fr !important;
}

.planning-calendar-monthly-config {
  margin-bottom: 1rem;
}

.hit-form-fixed-label-width :deep(.hit-fast-mode-browser) {
  width: 100%;
}

.custom-report-entity-field :deep(.hit-input) {
  width: 100%;
}

.custom-report-entity-field :deep(.hit-select) {
  width: 100%;
}
</style>
